"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { FavouriteJobResponseDto } from "@workspace/dto/job/favourite-job";
import type { UnfavouriteJobResponseDto } from "@workspace/dto/job/unfavourite-job";
import {
  Avatar,
  AvatarImage,
  InitialsAvatarFallback,
} from "@workspace/ui/components/avatar";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@workspace/ui/components/card";
import { TextWithTooltip } from "@workspace/ui/components/text-with-tooltip";
import { cn } from "@workspace/ui/lib/utils";
import type { AxiosResponse } from "axios";
import { formatDistance } from "date-fns";
import Link from "next/link";
import { useSession } from "next-auth/react";
import type { MouseEvent } from "react";
import { useState } from "react";
import { toast } from "sonner";
import { Icons } from "@/components/icons";
import { COMMITMENTS, WORK_MODELS } from "@/config/constants";
import { getApiErrorMessage, getApiErrorType } from "@/lib/api-error";
import { axios } from "@/lib/axios";
import { getBadgeColorVariant } from "@/lib/badge-color-variant";
import { formatCompensation } from "@/lib/compensation";
import { toastMutationNetworkError } from "@/lib/query-failure";
import { queryKeys } from "@/lib/query-keys";
import { buildListingUrl } from "@/lib/slugify";
import { LoginModal } from "../modals/auth/login";
import type { MarketplaceJob } from "./use-cluster";

type MarketplaceJobDetailsCardProps = {
  job: MarketplaceJob;
  cardClassName?: string;
  contentClassName?: string;
  footerClassName?: string;
  showContentBorder?: boolean;
  onLoginModalOpenChange?: (open: boolean) => void;
  loginPreferDialog?: boolean;
};

export function MarketplaceJobDetailsCard({
  job,
  cardClassName,
  contentClassName,
  footerClassName,
  showContentBorder = true,
  onLoginModalOpenChange,
  loginPreferDialog = false,
}: MarketplaceJobDetailsCardProps) {
  const queryClient = useQueryClient();
  const { status } = useSession();
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const listingHref = buildListingUrl(job.slug, job.title, job.postedBy ?? "");
  const posterName = job.postedBy ?? null;

  const handleLoginModalOpenChange = (open: boolean) => {
    setLoginModalOpen(open);
    onLoginModalOpenChange?.(open);
  };

  const applyOptimisticFavourite = (nextIsFavourited: boolean) => {
    queryClient.setQueriesData<MarketplaceJob[]>(
      { queryKey: queryKeys.marketplace.nearby() },
      (old) =>
        old?.map((j) =>
          j.id === job.id ? { ...j, isFavourited: nextIsFavourited } : j
        )
    );
  };

  const snapshotMarketplaceQueries = () =>
    queryClient.getQueriesData<MarketplaceJob[]>({
      queryKey: queryKeys.marketplace.nearby(),
    });

  const rollbackMarketplaceQueries = (
    previous: ReturnType<typeof snapshotMarketplaceQueries>
  ) => {
    for (const [key, data] of previous) {
      queryClient.setQueryData(key, data);
    }
  };

  const favouriteMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post<
        FavouriteJobResponseDto,
        AxiosResponse<FavouriteJobResponseDto>
      >(`/jobs/${job.id}/favourite-job`);
      return response.data;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.marketplace.nearby(),
      });
      const previous = snapshotMarketplaceQueries();
      applyOptimisticFavourite(true);
      return { previous };
    },
    onSuccess: () => {
      toast.success("Success.", {
        description: "Job added to favourites",
      });
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        rollbackMarketplaceQueries(context.previous);
      }
      if (toastMutationNetworkError(error, "Couldn't update favourites")) {
        return;
      }
      const type = getApiErrorType(error);
      if (type === "FAVOURITE_ALREADY_EXISTS") {
        toast(getApiErrorMessage(error) ?? "Already saved");
      } else {
        toast.error("Uh Oh! Something went wrong.", {
          description:
            getApiErrorMessage(error) ?? "Failed to add to favourites",
        });
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.marketplace.nearby(),
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.favourites.all }),
      ]);
    },
  });

  const unfavouriteMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post<
        UnfavouriteJobResponseDto,
        AxiosResponse<UnfavouriteJobResponseDto>
      >(`/jobs/${job.id}/unfavourite-job`);
      return response.data;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.marketplace.nearby(),
      });
      const previous = snapshotMarketplaceQueries();
      applyOptimisticFavourite(false);
      return { previous };
    },
    onSuccess: () => {
      toast.success("Success.", {
        description: "Job removed from favourites",
      });
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        rollbackMarketplaceQueries(context.previous);
      }
      if (toastMutationNetworkError(error, "Couldn't update favourites")) {
        return;
      }
      const type = getApiErrorType(error);
      if (type === "FAVOURITE_NOT_FOUND") {
        toast(getApiErrorMessage(error) ?? "Already removed");
      } else {
        toast.error("Uh Oh! Something went wrong.", {
          description:
            getApiErrorMessage(error) ?? "Failed to remove from favourites",
        });
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.marketplace.nearby(),
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.favourites.all }),
      ]);
    },
  });

  const isFavourited = job.isFavourited;

  const toggleFavourite = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (status === "unauthenticated") {
      handleLoginModalOpenChange(true);
      return;
    }

    if (isFavourited) {
      unfavouriteMutation.mutate();
    } else {
      favouriteMutation.mutate();
    }
  };

  return (
    <>
      <LoginModal
        isOpen={loginModalOpen}
        onOpenChange={handleLoginModalOpenChange}
        preferDialog={loginPreferDialog}
        returnTo={listingHref}
      />
      <Link href={listingHref} rel="noreferrer" target="_blank">
        <Card className={cn("overflow-hidden", cardClassName)}>
          <CardHeader className="flex flex-row items-start">
            <div className="flex flex-row items-start gap-3">
              <Avatar className="size-12 shrink-0 rounded-full">
                <AvatarImage
                  alt={posterName ?? undefined}
                  src={job.postedByImage ?? undefined}
                />
                <InitialsAvatarFallback name={posterName ?? ""} />
              </Avatar>
              <div className="grid min-w-0 gap-1">
                <CardTitle className="group flex items-center gap-2 text-lg">
                  <TextWithTooltip text={job.title} />
                </CardTitle>
                <CardDescription className="truncate">
                  Posted by {job.organizationName ?? posterName}
                </CardDescription>
              </div>
            </div>
            <div className="ml-auto flex items-center gap-1">
              <Button
                className="h-8 gap-1"
                onClick={toggleFavourite}
                size="icon"
                variant="outline"
              >
                <Icons.heart
                  className={cn(
                    "h-3.5 w-3.5 transition",
                    isFavourited
                      ? "fill-destructive text-destructive"
                      : "fill-transparent text-foreground"
                  )}
                />
              </Button>
            </div>
          </CardHeader>
          <CardContent
            className={cn(
              "p-4 text-sm",
              showContentBorder && "border-y",
              contentClassName
            )}
          >
            <ul className="grid gap-3">
              {job.storeName ? (
                <li className="flex items-center justify-between">
                  <span className="text-muted-foreground">Store</span>
                  <span>
                    <TextWithTooltip
                      className="max-w-64"
                      text={job.storeName}
                    />
                  </span>
                </li>
              ) : null}
              <li className="flex items-center justify-between">
                <span className="text-muted-foreground">Location</span>
                <span>
                  <TextWithTooltip
                    className="max-w-64"
                    text={`${job.address.street}, ${job.address.region}`}
                  />
                </span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-muted-foreground">Compensation</span>
                <span>{formatCompensation(job.compensation)}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-muted-foreground">Work model</span>
                <Badge
                  className="max-w-36 shrink-0 whitespace-nowrap"
                  variant={getBadgeColorVariant(job.work_model)}
                >
                  <span className="truncate">
                    {WORK_MODELS.find((w) => w.value === job.work_model)
                      ?.label ?? "-"}
                  </span>
                </Badge>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-muted-foreground">Commitment</span>
                <Badge
                  className="max-w-36 shrink-0 whitespace-nowrap"
                  variant={getBadgeColorVariant(job.commitment)}
                >
                  <span className="truncate">
                    {COMMITMENTS.find((c) => c.value === job.commitment)
                      ?.label ?? "-"}
                  </span>
                </Badge>
              </li>
            </ul>
          </CardContent>
          <CardFooter
            className={cn(
              "flex flex-row items-center justify-between",
              footerClassName
            )}
          >
            <div
              className="text-muted-foreground text-xs"
              suppressHydrationWarning
            >
              {`Posted ${formatDistance(job.createdAt, new Date(), { addSuffix: true })}`}
            </div>
            <Button asChild onClick={(e) => e.stopPropagation()} size="sm">
              <span>Apply</span>
            </Button>
          </CardFooter>
        </Card>
      </Link>
    </>
  );
}
